import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getNovelChapter } from "@/lib/novel";
import { generateChapterParagraphs } from "@/lib/readerContent";
import { useReaderSettings } from "@/hooks/useReaderSettings";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import ReaderSettingsPanel from "@/pages/read/components/ReaderSettingsPanel";

export default function ReadPage() {
  const { novelId = "", chapterId = "" } = useParams();
  const found = getNovelChapter(novelId, chapterId);
  const { settings, setFontSize, setTheme } = useReaderSettings();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const novel = found?.novel;
  const chapter = found?.chapter;
  const index = found?.index ?? 0;

  useDocumentMeta(
    novel && chapter ? `${chapter.title} - ${novel.title} - 御书屋` : "章节阅读 - 御书屋",
    novel && chapter
      ? `${novel.title} ${chapter.title}在线阅读，御书屋为你提供舒适的章节阅读体验与阅读设置。`
      : "御书屋章节在线阅读。",
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [novelId, chapterId]);

  const paragraphs = useMemo(() => {
    if (!novel || !chapter) return [];
    return generateChapterParagraphs(novel.id, index, chapter.title);
  }, [novel, chapter, index]);

  if (!novel || !chapter || paragraphs.length === 0) {
    return (
      <div className="w-full px-4 py-20 md:px-8">
        <div className="mx-auto flex max-w-xl flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-300">
            <i className="ri-file-warning-line"></i>
          </span>
          <h1 className="font-heading text-xl font-semibold text-foreground-800">
            章节内容暂不可用
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            该章节内容缺失或已移除，你可以返回目录选择其他章节。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {novel && (
              <Link
                to={`/detail/${novel.id}#chapters`}
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                返回目录
              </Link>
            )}
            <Link
              to="/"
              className="whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const total = novel.chapters.length;
  const prev = index > 0 ? novel.chapters[index - 1] : null;
  const next = index < total - 1 ? novel.chapters[index + 1] : null;
  const progress = ((index + 1) / total) * 100;
  const isDark = settings.theme === "dark";

  const containerClass = isDark
    ? "bg-foreground-950 text-foreground-100"
    : settings.theme === "eye"
      ? "bg-accent-50 text-foreground-900"
      : "bg-background-50 text-foreground-900";

  const navBtnClass = isDark
    ? "border-foreground-300 text-foreground-200 hover:bg-foreground-900 disabled:opacity-40 disabled:hover:bg-transparent"
    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600 disabled:opacity-40";

  const mutedClass = isDark ? "text-foreground-400" : "text-foreground-500";
  const bodyClass = isDark ? "text-foreground-200" : "text-foreground-800";

  return (
    <div className={`min-h-[70vh] w-full transition-colors ${containerClass}`}>
      <div className="h-1 w-full bg-background-300/40">
        <div className="h-full bg-accent-500 transition-all" style={{ width: `${progress}%` }}></div>
      </div>

      <div className="w-full px-4 py-6 md:px-8 md:py-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <nav className={`flex items-center gap-2 text-sm ${mutedClass}`}>
            <Link to="/" className="transition-colors hover:text-accent-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/detail/${novel.id}`}
              className="max-w-[140px] truncate transition-colors hover:text-accent-600 sm:max-w-none"
            >
              {novel.title}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="truncate">第 {index + 1} 章</span>
          </nav>

          <div className="relative">
            <button
              type="button"
              onClick={() => setSettingsOpen((prev) => !prev)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-4 py-2 text-sm font-medium transition-colors ${navBtnClass}`}
            >
              <i className="ri-settings-3-line"></i>
              阅读设置
            </button>
            {settingsOpen && (
              <ReaderSettingsPanel
                settings={settings}
                onFontSize={setFontSize}
                onTheme={setTheme}
                onClose={() => setSettingsOpen(false)}
              />
            )}
          </div>
        </div>

        <article className="mx-auto max-w-3xl">
          <h1 className="font-heading text-center text-xl font-bold md:text-2xl">
            {chapter.title}
          </h1>
          <p className={`mt-2 text-center text-xs ${mutedClass}`}>
            {novel.title} · {novel.author} · 第 {index + 1} / {total} 章
          </p>

          <div className="mt-8" style={{ fontSize: `${settings.fontSize}px`, lineHeight: 2 }}>
            {paragraphs.map((paragraph, i) => (
              <p key={i} className={`mb-5 leading-loose ${bodyClass}`}>
                {paragraph}
              </p>
            ))}
          </div>
        </article>

        <div className="mx-auto mt-10 flex max-w-3xl flex-col gap-3 border-t border-background-300/40 pt-8 sm:flex-row sm:items-center sm:justify-between">
          {prev ? (
            <Link
              to={`/read/${novel.id}/${prev.chapterId}`}
              className={`flex items-center gap-2 rounded-md border px-5 py-3 text-sm font-medium transition-colors ${navBtnClass}`}
            >
              <i className="ri-arrow-left-line"></i>
              上一章
            </Link>
          ) : (
            <span className={`flex items-center gap-2 rounded-md border px-5 py-3 text-sm opacity-40 ${navBtnClass}`}>
              <i className="ri-arrow-left-line"></i>
              上一章
            </span>
          )}

          <Link
            to={`/detail/${novel.id}#chapters`}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-list-check-2"></i>
            返回目录
          </Link>

          {next ? (
            <Link
              to={`/read/${novel.id}/${next.chapterId}`}
              className={`flex items-center justify-end gap-2 rounded-md border px-5 py-3 text-sm font-medium transition-colors ${navBtnClass}`}
            >
              下一章
              <i className="ri-arrow-right-line"></i>
            </Link>
          ) : (
            <span className={`flex items-center justify-end gap-2 rounded-md border px-5 py-3 text-sm opacity-40 ${navBtnClass}`}>
              下一章
              <i className="ri-arrow-right-line"></i>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}